import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin, googleLogout } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { setOrgCreated } from "../../redux/slices/orgSlice";

export interface DecodedToken {
  email: string;
  name: string;
  picture?: string;
  exp?: number;
}

const CLIENT_ID = "704273243272-83o6oldgf72r8p31jmvcldbh3anuuf7m.apps.googleusercontent.com";

const Login = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("userEmail");
    const dp = urlParams.get("userDP");
    const presentationId = urlParams.get("presentationId");

    if (email) {
      const fakeToken: DecodedToken = {
        email,
        name: email.split("@")[0],
        picture: dp || "",
      };
      sessionStorage.setItem("userEmail", email);
      sessionStorage.setItem("userDP", dp || "");
      if (presentationId) sessionStorage.setItem("presentationId", presentationId);
      setUser(fakeToken);
      setIsAuthenticated(true);
      saveUserData(fakeToken);
    } else {
      const token = localStorage.getItem("token");
      checkTokenExpiry(token);
    }
  }, []);

  const checkTokenExpiry = (token: string | null) => {
    if (!token) return;
    try {
      const decoded: DecodedToken = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      if (decoded.exp && decoded.exp > currentTime) {
        const timeoutDuration = (decoded.exp - currentTime) * 1000;
        setTimeout(() => {
          handleLogout();
        }, timeoutDuration);
      } else {
        handleLogout();
      }
    } catch {
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    sessionStorage.clear();
    window.location.reload();
  };

  const saveUserData = async (userData: DecodedToken) => {
    setIsLoading(true);
    const generatedOrgId = `Parati-${Date.now()}`;

    try {
      const ipInfoResponse = await fetch("https://api.zynth.ai/api/v1/data/userprofile/ip-info");
      const ipInfoData = await ipInfoResponse.json();

      const userPayload = {
        userId: userData.email,
        name: userData.name,
        orgId: generatedOrgId,
        pptCount: 0,
        userIPCountry: ipInfoData.country,
        signupLink: "googleslideextension",
        source: "extension",
        currentSource: "extension",
      };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/data/userprofile/user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userPayload),
      });

      const responseData = await res.json();
      sessionStorage.setItem("authToken", responseData.token);
      sessionStorage.setItem("orgId", responseData.orgid || generatedOrgId);

      const from = new URLSearchParams(window.location.search).get("from");
      const presentationId = sessionStorage.getItem("presentationId");
      const userEmail = sessionStorage.getItem("userEmail");
      const userDP = sessionStorage.getItem("userDP");

      if (from === "refine") {
        console.log("🧭 Directly navigating to /refine-presentation");
        navigate("/refine-presentation");
        return;
      }

      // fallback for standalone or different flows
      if (res.status === 200) dispatch(setOrgCreated(true));
      if (responseData.orgid) navigate("/new-presentation");
      else navigate("/onboarding");

    } catch (error) {
      console.error("Error storing user data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = (credentialResponse: any) => {
    if (credentialResponse?.credential) {
      const decoded: DecodedToken = jwtDecode(credentialResponse.credential);
      sessionStorage.setItem("userEmail", decoded.email);
      sessionStorage.setItem("userDP", decoded.picture || "");
      setUser(decoded);
      setIsAuthenticated(true);
      saveUserData(decoded);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen">
      {isLoading ? (
        <div>Loading...</div>
      ) : isAuthenticated ? (
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Welcome, {user?.name}!</h2>
          <img src={user?.picture} alt="Profile" className="w-20 h-20 rounded-full mb-4" />
          <button
            onClick={() => {
              googleLogout();
              sessionStorage.clear();
              setIsAuthenticated(false);
            }}
            className="bg-red-500 text-white px-6 py-2 rounded-md"
          >
            Logout
          </button>
        </div>
      ) : (
        <GoogleOAuthProvider clientId={CLIENT_ID}>
          <h2 className="text-2xl font-bold mb-4">Login to Continue</h2>
          <GoogleLogin onSuccess={handleGoogleSuccess} />
        </GoogleOAuthProvider>
      )}
    </div>
  );
};

export default Login;
